<script lang="ts" setup>
import { Heading } from '~ds/components/ui/typography';

export interface KUICoreRegionSwitcherOption {
  label: string;
  url: string;
  isCurrent: boolean;
}

interface Props {
  regions: KUICoreRegionSwitcherOption[];
  heading: string;
  /** Accessible name applied to the nav landmark (already translated). */
  navLabel: string;
}

defineProps<Props>();

const emit = defineEmits<{
  /**
   * The click event travels with the region so a caller running its own switch
   * flow can `preventDefault()` the link once it has taken the click over.
   */
  switch: [region: KUICoreRegionSwitcherOption, event: MouseEvent];
}>();

// The light focus ring is legible on the dark footer, where the global outline is not.
const REGION_CLASS = 'text-background focus-visible:ring-background focus-visible:ring-offset-foreground cursor-pointer text-left text-lg focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none rounded';
</script>

<template>
  <nav :aria-label="navLabel" class="flex flex-col gap-2">
    <Heading
      v-if="heading"
      weight="normal"
      size="sm"
      as="h5"
      class="text-background mb-2 tracking-wider uppercase"
    >
      {{ heading }}
    </Heading>
    <ul class="flex flex-col gap-1">
      <li
        v-for="region in regions"
        :key="region.url"
        class="flex items-center gap-2"
      >
        <NuxtLink
          :to="region.url"
          external
          :aria-current="region.isCurrent ? 'true' : undefined"
          :class="[REGION_CLASS, region.isCurrent ? 'font-medium underline' : '']"
          @click="emit('switch', region, $event)"
        >
          {{ region.label }}
        </NuxtLink>
      </li>
    </ul>
  </nav>
</template>
